/**
 * Bluetooth Terminal class.
 */
export default class BluetoothTerminal {
  /**
   * Create preconfigured Bluetooth Terminal instance.
   * @param {!(number|string)} [serviceUuid=0xFFE0] - Service UUID
   * @param {!(number|string)} [characteristicUuid=0xFFE1] - Characteristic UUID
   * @param {string} [receiveSeparator='\n'] - Receive separator
   * @param {string} [sendSeparator='\n'] - Send separator
   */
  constructor(serviceUuid = "6e400001-b5a3-f393-e0a9-e50e24dcca9e", characteristicUuid = "6e400003-b5a3-f393-e0a9-e50e24dcca9e", rxCharacteristicUuid = "6e400002-b5a3-f393-e0a9-e50e24dcca9e",
      receiveSeparator = '\n', sendSeparator = '\n') {
    // Used private variables.
    this._receiveBuffer = ''; // Buffer containing not separated data.
    this._maxCharacteristicValueLength = 20; // Max characteristic value length.
    this._device = null; // Device object cache.
    this._characteristic = null; // Characteristic object cache.
    this._currentVersion = null;
    this._firstEnter = false;
    this._secondEnter = false;
    this._onboardBlocked = false;
    this._onboardEnterResponses = [1, 1];
    this._isSendingData = false;
    this._sendQueue = Promise.resolve();
    this._reconnectInProgress = false; // re-entry guard for _handleDisconnection
    this._atOnboardTimer = null; // Stability-check timer for AT#ONBOARD (see _handleCharacteristicValueChanged)
    this._atOnboardTimerSource = null; // which handler set the timer: 'waiting' | 'ble-hid' | 'version' | 'reconnect'
    this._atOnboardPending = false; // true when AT#ONBOARD was deferred due to BLE HID warning; re-sent after reconnect
    this._connectionFallbackTimer = null; // Reconnect trigger if "Waiting for instructions" is never received
    this._waitingForInstructionsMisses = 0; // Consecutive times the message was missed; resets on receipt
    this._bleHidWarningCount = 0;        // counts "BLE HID is not connected" occurrences
    this._bleHidWarningWindowStart = null; // timestamp of first occurrence in current window
    this._resetLoopDetected = false;     // true once the BLE reset loop is confirmed
    this._stopReconnecting = false;      // prevents auto-reconnect while loop is active
    // Bound functions used to add and remove appropriate event handlers.
    this._boundHandleDisconnection = this._handleDisconnection.bind(this);
    this._boundHandleCharacteristicValueChanged =
        this._handleCharacteristicValueChanged.bind(this);

    // Configure with specified parameters.
    this.setServiceUuid(serviceUuid);
    this.setCharacteristicUuid(characteristicUuid);
    this.setRXCharacteristicUuid(rxCharacteristicUuid);
    this.setReceiveSeparator(receiveSeparator);
    this.setSendSeparator(sendSeparator);
  }

  /**
   * Set number or string representing service UUID used.
   * @param {!(number|string)} uuid - Service UUID
   */
  setServiceUuid(uuid) {
    if (!Number.isInteger(uuid) &&
        !(typeof uuid === 'string' || uuid instanceof String)) {
      throw new Error('UUID type is neither a number nor a string');
    }

    if (!uuid) {
      throw new Error('UUID cannot be a null');
    }

    this._serviceUuid = uuid;
  }

  /**
   * Set number or string representing characteristic UUID used.
   * @param {!(number|string)} uuid - Characteristic UUID
   */
  setCharacteristicUuid(uuid) {
    if (!Number.isInteger(uuid) &&
        !(typeof uuid === 'string' || uuid instanceof String)) {
      throw new Error('UUID type is neither a number nor a string');
    }

    if (!uuid) {
      throw new Error('UUID cannot be a null');
    }

    this._characteristicUuid = uuid;
  }
  /**
   * Set number or string representing characteristic UUID used.
   * @param {!(number|string)} uuid - Characteristic UUID
   */
   setRXCharacteristicUuid(uuid) {
    if (!Number.isInteger(uuid) &&
        !(typeof uuid === 'string' || uuid instanceof String)) {
      throw new Error('UUID type is neither a number nor a string');
    }

    if (!uuid) {
      throw new Error('UUID cannot be a null');
    }

    this._rxCharacteristicUuid = uuid;
  }

  /**
   * Set character representing separator for data coming from the connected
   * device, end of line for example.
   * @param {string} separator - Receive separator with length equal to one
   *                             character
   */
  setReceiveSeparator(separator) {
    if (!(typeof separator === 'string' || separator instanceof String)) {
      throw new Error('Separator type is not a string');
    }

    if (separator.length !== 1) {
      throw new Error('Separator length must be equal to one character');
    }

    this._receiveSeparator = separator;
  }

  /**
   * Set string representing separator for data coming to the connected
   * device, end of line for example.
   * @param {string} separator - Send separator
   */
  setSendSeparator(separator) {
    if (!(typeof separator === 'string' || separator instanceof String)) {
      throw new Error('Separator type is not a string');
    }

    if (separator.length !== 1) {
      throw new Error('Separator length must be equal to one character');
    }

    this._sendSeparator = separator;
  }

  /**
   * Launch Bluetooth device chooser and connect to the selected device.
   * @return {Promise} Promise which will be fulfilled when notifications will
   *                   be started or rejected if something went wrong
   */
  connect() {
    this.resetOnboardState();
    return this._connectToDevice(this._device);
  }

  /**
   * Disconnect from the connected device.
   */
  disconnect() {
    this._disconnectFromDevice(this._device);

    if (this._characteristic) {
      this._characteristic.removeEventListener('characteristicvaluechanged',
          this._boundHandleCharacteristicValueChanged);
      this._characteristic = null;
    }

    this._device = null;
  }

  /**
   * Data receiving handler which called whenever the new data comes from
   * the connected device, override it to handle incoming data.
   * @param {string} data - Data
   */
  receive(data) {
    // Handle incoming data.
  }

  /**
   * Send data to the connected device.
   * @param {string} data - Data
   * @return {Promise} Promise which will be fulfilled when data will be sent or
   *                   rejected if something went wrong
   */
  send(data) {
    const next = this._sendQueue.then(
      () => this._doSend(data),
      () => this._doSend(data),
    );
    this._sendQueue = next.catch(() => {});
    return next;
  }

  _doSend(data) {
    // Convert data to the string using global object.
    data = String(data || '');
    const ts = new Date().toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
    console.log(`[${ts}] SEND_TO_DEVICE:`, data);

    // Return rejected promise immediately if data is empty.
    if (!data) {
      return Promise.reject(new Error('Data must be not empty'));
    }

    data += this._sendSeparator;

    // Split data to chunks by max characteristic value length.
    const chunks = this.constructor._splitByLength(data,
        this._maxCharacteristicValueLength);

    // Return rejected promise immediately if there is no connected device.
    if (!this._rxCharacteristic) {
      return Promise.reject(new Error('There is no connected device'));
    }

    // Write first chunk to the characteristic immediately.
    let promise = this._writeToCharacteristic(this._rxCharacteristic, chunks[0]);

    // Iterate over chunks if there are more than one of it.
    for (let i = 1; i < chunks.length; i++) {
      // Chain new promise.
      promise = promise.then(() => new Promise((resolve, reject) => {
        // Reject promise if the device has been disconnected.
        if (!this._rxCharacteristic) {
          reject(new Error('Device has been disconnected'));
        }

        // Write chunk to the characteristic and resolve the promise.
        this._writeToCharacteristic(this._rxCharacteristic, chunks[i])
            .then(resolve)
            .catch(reject);
      }));
    }

    return promise;
  }

  resetOnboardState() {
    this._onboardBlocked = false;
    this._firstEnter = false;
    this._secondEnter = false;
    this._onboardEnterResponses = [1, 1];
    if (this._atOnboardTimer) {
      clearTimeout(this._atOnboardTimer);
      this._atOnboardTimer = null;
    }
    this._atOnboardTimerSource = null;
    if (this._connectionFallbackTimer) {
      clearTimeout(this._connectionFallbackTimer);
      this._connectionFallbackTimer = null;
    }
    this._waitingForInstructionsMisses = 0;
    this._bleHidWarningCount = 0;
    this._bleHidWarningWindowStart = null;
    this._resetLoopDetected = false;
    this._stopReconnecting = false;
    this._atOnboardPending = false;
  }

  /**
   * Resume reconnecting after a BLE reset loop was detected and the user has
   * restarted the device.  Clears the loop-detection state and attempts to
   * reconnect to the already-selected device.
   * @return {Promise}
   */
  resumeAfterResetLoop() {
    this._stopReconnecting = false;
    this._resetLoopDetected = false;
    this._bleHidWarningCount = 0;
    this._bleHidWarningWindowStart = null;
    if (this._device) {
      return this._connectDeviceAndCacheCharacteristic(this._device)
        .then((characteristic) => this._startNotifications(characteristic));
    }
    return Promise.reject(new Error('No device selected — cannot reconnect'));
  }

  /**
   * Get the connected device name.
   * @return {string} Device name or empty string if not connected
   */
  getDeviceName() {
    if (!this._device) {
      return '';
    }

    return this._device.name;
  }

  /**
   * Connect to device.
   * @param {Object} device
   * @return {Promise}
   * @private
   */
  _connectToDevice(device) {
    return (device ? Promise.resolve(device) : this._requestBluetoothDevice())
        .then((device) => this._connectDeviceAndCacheCharacteristic(device))
        .catch((error) => {
          this._log(error);
          return Promise.reject(error);
        });
  }

  /**
   * Disconnect from device.
   * @param {Object} device
   * @private
   */
  _disconnectFromDevice(device) {
    if (!device) {
      return;
    }

    this._log('Disconnecting from "' + device.name + '" bluetooth device...');

    device.removeEventListener('gattserverdisconnected',
        this._boundHandleDisconnection);

    if (!device.gatt.connected) {
      this._log('"' + device.name +
          '" bluetooth device is already disconnected');
      return;
    }

    device.gatt.disconnect();

    this._log('"' + device.name + '" bluetooth device disconnected');
  }

  /**
   * Request bluetooth device.
   * @return {Promise}
   * @private
   */
  _requestBluetoothDevice() {
    this._log('Requesting bluetooth device...');

    return navigator.bluetooth.requestDevice({
      // acceptAllDevices: true,
      filters: [{services: [this._serviceUuid]}],
    })
        .then((device) => {
          this._log('"' + device.name + '" bluetooth device selected');

          this._device = device; // Remember device.
          this._device.addEventListener('gattserverdisconnected',
              this._boundHandleDisconnection);

          return this._device;
        });
  }

  /**
   * Connect device and cache characteristic.
   * @param {Object} device
   * @return {Promise}
   * @private
   */
  _connectDeviceAndCacheCharacteristic(device, settleDelay = 800) {
    // Check remembered characteristic.
    if (device.gatt.connected && this._characteristic) {
      return Promise.resolve(this._characteristic);
    }

    this._log('Connecting to GATT server...');

    return device.gatt.connect()
        .then((server) => {
          this._log('GATT server connected', 'Getting service...');
          // Chrome bug workaround: after a peripheral-initiated reconnect,
          // calling getPrimaryService too quickly can return a handle that
          // never resolves (indefinitely hung Promise). The settle delay gives
          // Chrome's internal GATT state time to flush the stale connection.
          // On the first attempt we use 800 ms (fast happy path). Retries use
          // longer delays (2500 ms, 4000 ms) because some Bluetooth adapters
          // need significantly more time to clear their GATT cache — especially
          // after an AT#ONBOARD-triggered BLE reset on a new/unpaired computer.
          return new Promise((r) => setTimeout(() => r(server), settleDelay));
        })
        .then((server) => server.getPrimaryService(this._serviceUuid))
        .then((service) => {
          this._log('Service found', 'Getting TX characteristic...');
          this._service = service;
          return service.getCharacteristic(this._characteristicUuid);
        })
        .then((characteristic) => {
          this._log('Characteristic found');

          this._characteristic = characteristic;

          // Subscribe to notifications NOW — before fetching RX — so the listener
          // is in place as early as possible and "Waiting for instructions" is not
          // missed while the RX characteristic is being fetched.
          return this._startNotifications(characteristic).then(() => characteristic);
        })
        .then(() => {
          this._log('Service found', 'Getting RX characteristic...');

          return this._service.getCharacteristic(this._rxCharacteristicUuid);
        })
        .then((characteristic) => {
          this._log('Characteristic found');

          this._rxCharacteristic = characteristic;

          return this._characteristic;
        });
  }

  /**
   * Start notifications.
   * @param {Object} characteristic
   * @return {Promise}
   * @private
   */
  _startNotifications(characteristic) {
    this._log('Starting notifications...');

    return characteristic.startNotifications()
        .then(() => {
          this._log('Notifications started');

          characteristic.addEventListener('characteristicvaluechanged',
              this._boundHandleCharacteristicValueChanged);

          // Guard: "Waiting for instructions" may have been sent by the device
          // during GATT setup — before this listener was attached.
          //
          // Two cases when this timer fires without having received the message:
          //
          // A) RX characteristic IS cached (_rxCharacteristic set): the full
          //    connection completed but we just missed the message in the GATT
          //    race.  The device is alive in enter_user_request() waiting for a
          //    command.  Send AT#ONBOARD directly — no disconnect needed, and no
          //    UI disruption.  This is the common case that was causing the
          //    "connects then immediately disconnects" issue on the home page.
          //
          // B) RX characteristic NOT yet cached: the race happened before the
          //    listener was ready.  Force a disconnect so _handleDisconnection
          //    reconnects; on reconnect the browser uses cached GATT descriptors
          //    (near-instant) so the listener is ready before the device
          //    re-sends its greeting.  Give up after 2 consecutive misses.
          if (!this._onboardBlocked) {
            if (this._connectionFallbackTimer) clearTimeout(this._connectionFallbackTimer);
            this._connectionFallbackTimer = setTimeout(() => {
              this._connectionFallbackTimer = null;
              if (this._onboardBlocked || this._atOnboardTimer || this._resetLoopDetected) return;

              if (this._rxCharacteristic && this._device?.gatt?.connected) {
                // Case A: device is fully ready — send AT#ONBOARD directly.
                console.log('"Waiting for instructions" missed but device is ready — sending AT#ONBOARD directly');
                this._waitingForInstructionsMisses = 0;
                this._firstEnter = false;
                this._secondEnter = false;
                this._atOnboardTimerSource = 'waiting';
                this._atOnboardTimer = setTimeout(() => {
                  this._atOnboardTimer = null;
                  this._atOnboardTimerSource = null;
                  if (this._onboardBlocked || this._resetLoopDetected) return;
                  this.send('AT#ONBOARD')
                    .catch(err => console.log('AT#ONBOARD (missed-waiting-direct) ERROR:', err.message || err));
                }, 300);
                return;
              }

              // Case B: RX not yet cached — force reconnect.
              this._waitingForInstructionsMisses++;
              if (this._waitingForInstructionsMisses <= 2) {
                console.log(`"Waiting for instructions" not received — reconnecting to recover (attempt ${this._waitingForInstructionsMisses})`);
                if (this._device?.gatt?.connected) this._device.gatt.disconnect();
              } else {
                console.log('"Waiting for instructions" never received after retries');
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('device-error', {
                    detail: { message: 'Device not responding. Please restart the device and try again.' }
                  }));
                }
              }
            }, 3000);
          }
        });
  }

  /**
   * Stop notifications.
   * @param {Object} characteristic
   * @return {Promise}
   * @private
   */
  _stopNotifications(characteristic) {
    this._log('Stopping notifications...');

    return characteristic.stopNotifications()
        .then(() => {
          this._log('Notifications stopped');

          characteristic.removeEventListener('characteristicvaluechanged',
              this._boundHandleCharacteristicValueChanged);
        });
  }

  /**
   * Handle disconnection.
   * @param {Object} event
   * @private
   */
  _handleDisconnection(event) {
    const device = event.target;

    // Null all cached GATT state so any in-flight send fails fast and the next
    // reconnect performs a fresh service/characteristic discovery. Keeping a
    // stale `_characteristic`/`_service` after a peripheral-initiated disconnect
    // (e.g. BLE auto-reset) can cause Chrome's getPrimaryService to hang.
    this._rxCharacteristic = null;
    this._characteristic = null;
    this._service = null;

    // Reset Enter: response tracking so the auto-responder fires correctly on
    // the next reconnect's prompt sequence.  These flags are also cleared inside
    // the AT#ONBOARD timer callbacks, but loadSavedDataClick sends AT#ONBOARD
    // directly via sendCommand() (no timer), so they would otherwise stay true
    // from the first onboarding cycle and silently suppress the auto-responder
    // during a redo/load cycle.
    this._firstEnter = false;
    this._secondEnter = false;

    // Cancel any pending timers — the connection just dropped.
    if (this._atOnboardTimer) {
      clearTimeout(this._atOnboardTimer);
      this._atOnboardTimer = null;
      this._atOnboardTimerSource = null;
    }
    if (this._connectionFallbackTimer) {
      clearTimeout(this._connectionFallbackTimer);
      this._connectionFallbackTimer = null;
    }

    // Re-entry guard: when the retry-on-timeout logic below force-disconnects a
    // hung reconnect attempt, the gattserverdisconnected event fires again. Let
    // the existing retry loop handle reconnection rather than spawning a parallel one.
    if (this._reconnectInProgress) {
      return;
    }

    // If a reset loop was detected, stop reconnecting automatically.
    // The UI will prompt the user to restart the device and then call
    // resumeAfterResetLoop() to try again.
    if (this._stopReconnecting) {
      this._log('Reconnect suppressed — BLE reset loop detected. Waiting for user to restart the device.');
      return;
    }

    this._log('"' + device.name +
        '" bluetooth device disconnected, trying to reconnect...');

    // Notify UI: enter the "reconnecting" state. Listeners should disable any
    // command-sending buttons / show a status indicator until either
    // `device-reconnected` or `device-reconnect-failed` fires.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('device-reconnecting'));
    }

    // Retry-with-timeout wrapper: getPrimaryService can hang indefinitely after a
    // peripheral-initiated disconnect (BLE auto-reset). Force a clean GATT
    // disconnect and try again instead of waiting forever.
    //
    // ── Platform differences ──────────────────────────────────────────────────
    // macOS / CoreBluetooth: GATT service handles are cached in-process. After
    // reconnect, getPrimaryService() resolves in < 1 s. A short settle delay
    // (800 ms) and a 10 s timeout are sufficient.
    //
    // Windows / WinRT Bluetooth API: Even for a previously-paired device, Windows
    // must re-validate its cached GATT service handles against the new connection
    // after a peripheral-initiated BLE reset. This re-validation runs in the WinRT
    // stack and can take 15–30 s — it starts as soon as gatt.connect() resolves
    // and runs in parallel with our settle delay. The old code worked because it
    // had NO timeout on getPrimaryService() and simply waited it out. Our new
    // 10 s timeout cuts the re-validation off every time, producing 3 consecutive
    // timeouts and "Could not reconnect."
    //
    // Fix: keep the same modest settle delays on Windows (the re-validation runs
    // in the background regardless), but use a 45 s timeout to let WinRT finish.
    //
    // Progressive strategy:
    //  Both — Attempt 1:  800 ms settle
    //         Attempt 2: 2500 ms settle, 3 s retry wait
    //         Attempt 3: 4000 ms settle, 5 s retry wait
    //  macOS  — timeout: 10 s per attempt
    //  Windows— timeout: 45 s per attempt (WinRT GATT re-validation can take 15–30 s)
    const isWindows = typeof navigator !== 'undefined' && /Windows/i.test(navigator.userAgent);
    if (isWindows) {
      console.log('Windows detected — using extended getPrimaryService() timeout (WinRT GATT re-validation can take 15–30 s)');
    }
    const attemptReconnect = (attempt) => {
      // macOS (CoreBluetooth): GATT handles are in-process cached; 800 ms settle
      // before getPrimaryService() is enough and the 10 s timeout is sufficient.
      //
      // Windows (WinRT): two known failure modes for previously-paired devices:
      //  a) Windows auto-reconnect: the OS re-establishes a BLE connection during
      //     our 6 s wait (Windows bonds to devices and reconnects aggressively on
      //     some adapters). Chrome then calls gatt.connect() into an OS-owned GATT
      //     session → getPrimaryService() hangs indefinitely. Fix: explicitly call
      //     gatt.disconnect() first to break the OS session and let Chrome own it.
      //     Skip the settle delay so Chrome calls getPrimaryService() before
      //     Windows can interfere again.
      //  b) Slow WinRT GATT re-validation: even with a cached DB, the OS can take
      //     15–30 s to re-validate handles for a new connection. Fix: 45 s timeout.
      const settleDelay = isWindows ? 0 : (attempt === 1 ? 800 : attempt === 2 ? 2500 : 4000);
      const timeoutMs = isWindows ? 45000 : 10000;
      console.log(`Reconnect attempt ${attempt}/3 — settleDelay=${settleDelay}ms, timeout=${timeoutMs}ms${isWindows ? ' (Windows)' : ''}`);
      // On Windows: break any OS-managed GATT session before connecting so Chrome
      // gets a clean, Chrome-owned session rather than inheriting a stale one.
      const preConnectDisconnect = isWindows
        ? new Promise((r) => {
            try { if (device.gatt.connected) { device.gatt.disconnect(); } } catch (e) {}
            setTimeout(r, 500); // brief pause for the disconnect to settle
          })
        : Promise.resolve();
      let timedOut = false;
      const timeoutP = new Promise((_, reject) => {
        setTimeout(() => {
          timedOut = true;
          reject(new Error('reconnect attempt timed out after ' + timeoutMs + 'ms'));
        }, timeoutMs);
      });
      return Promise.race([
        preConnectDisconnect.then(() => this._connectDeviceAndCacheCharacteristic(device, settleDelay)),
        timeoutP,
      ]).catch((err) => {
        if (timedOut && attempt < 3) {
          try { if (device.gatt.connected) device.gatt.disconnect(); } catch (e) {}
          this._rxCharacteristic = null;
          this._characteristic = null;
          this._service = null;
          const retryWait = attempt === 1 ? 3000 : 5000;
          console.log(`Reconnect attempt ${attempt} timed out — waiting ${retryWait}ms before retry`);
          return new Promise((r) => setTimeout(r, retryWait)).then(() => attemptReconnect(attempt + 1));
        }
        throw err;
      });
    };
    this._reconnectInProgress = true;
    setTimeout(() => {
      attemptReconnect(1)
        .then(() => {
            this._reconnectInProgress = false;
            this._log('Reconnected successfully');
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('device-reconnected'));
            }
            if (this._onboardCompleteResolve) {
              console.log('Device reconnected — treating as onboarding confirmation');
              this._onboardBlocked = true;
              this._onboardCompleteResolve(true);
              this._onboardCompleteResolve = null;
            } else if (this._atOnboardPending && !this._onboardBlocked) {
              // AT#ONBOARD was deferred because the BLE HID reset fired before the
              // 1.5 s stability window expired. Now that the reconnect completed,
              // wait 1.5 s for the device to settle then send it — don't wait for a
              // second "Waiting for instructions" that may never arrive.
              this._atOnboardPending = false;
              this._atOnboardTimerSource = 'reconnect';
              this._atOnboardTimer = setTimeout(() => {
                this._atOnboardTimer = null;
                this._atOnboardTimerSource = null;
                if (this._onboardBlocked || this._resetLoopDetected) return;
                this._firstEnter = false;
                this._secondEnter = false;
                console.log('AT#ONBOARD retry after BLE HID reset cycle completed');
                this.send('AT#ONBOARD')
                  .catch((err) => console.log('AT#ONBOARD retry ERROR:', err.message || err));
              }, 1500);
            }
          })
        .catch((error) => {
          this._reconnectInProgress = false;
          this._log(error);
          this._log('Could not reconnect to device.');
          if (typeof window !== 'undefined') {
            // Emit both: legacy `device-error` for existing listeners and the
            // new `device-reconnect-failed` so safety-gated UI can react
            // specifically to "the device is gone, don't accept commands".
            window.dispatchEvent(new CustomEvent('device-reconnect-failed', {
              detail: { message: 'Could not reconnect to device.' }
            }));
            window.dispatchEvent(new CustomEvent('device-error', {
              detail: { message: 'Could not reconnect to device.' }
            }));
          }
        });
    }, 6000);
  }

  /**
   * Handle characteristic value changed.
   * @param {Object} event
   * @private
   */
  async _handleCharacteristicValueChanged(event) {
    const value = new TextDecoder().decode(event.target.value);
    if (!/^#\d/.test(value)) this._log('From device:', value);

    // Any message means the device is active — cancel the reconnect fallback.
    if (this._connectionFallbackTimer) {
      clearTimeout(this._connectionFallbackTimer);
      this._connectionFallbackTimer = null;
      this._waitingForInstructionsMisses = 0;
    }

    const isDeviceError = String(value)?.includes('ERROR') || /no imu samples/i.test(value);
    if (isDeviceError && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('device-error', { detail: { message: value.trim() } }));
    }

    if ((String(value)?.includes('ONBOARDING COMPLETED'))) {
      console.log('ONBOARDING COMPLETED');
      this._onboardBlocked = true
      if (this._onboardCompleteResolve) {
        this._onboardCompleteResolve(true);
        this._onboardCompleteResolve = null;
      }
    }

    // ── AT#ONBOARD confirmed — device is live in onboarding mode ─────────────
    // The device sends "#ONBOARD_READY" as its very first BLE UART message after
    // the AT#ONBOARD-triggered reinit reconnect.  This tells us definitively that:
    //   1. The reconnect was intentional (not a generic BLE drop).
    //   2. The Enter: auto-responder should start fresh — reset tracking flags so
    //      a redo/load cycle's _onboardEnterResponses[0] goes to the first prompt.
    //   3. We can cancel any pending reconnect-path AT#ONBOARD timer — the device
    //      has already entered onboarding mode, we don't need to send it again.
    if (String(value)?.includes('#ONBOARD_READY')) {
      console.log('#ONBOARD_READY received — device is in onboarding mode');
      // Ensure the auto-responder is primed for the coming prompt sequence.
      // (These are also reset in _handleDisconnection, but this is a belt-and-
      // suspenders guard for any race where the disconnect event was missed.)
      this._firstEnter = false;
      this._secondEnter = false;
      // Cancel any pending reconnect-path timer — device is already ready.
      if (this._atOnboardTimer) {
        clearTimeout(this._atOnboardTimer);
        this._atOnboardTimer = null;
        this._atOnboardTimerSource = null;
      }
      this._atOnboardPending = false;
    }

    if (String(value)?.includes('#VERSION=')) {
      this._currentVersion = value.replace('#VERSION=', '').trim();
      console.log('this._currentVersion ===', this._currentVersion, 'current value:', String(value));

      // ── Version response → AT#ONBOARD trigger ────────────────────────────
      // If AT#ONBOARD is pending (deferred because "BLE HID is not connected"
      // arrived before the connection was stable), the device has just proved
      // it is alive and responsive on UART.  Fire AT#ONBOARD now with the
      // same 1.5 s stability delay rather than waiting for the full 6 s window
      // to elapse — this is the primary fix for the case where repeated
      // "BLE HID is not connected" messages kept resetting the 6 s timer.
      if (this._atOnboardPending && !this._onboardBlocked) {
        if (this._atOnboardTimer) {
          clearTimeout(this._atOnboardTimer);
          this._atOnboardTimer = null;
          this._atOnboardTimerSource = null;
        }
        this._atOnboardPending = false;
        console.log('AT#ONBOARD triggered by #VERSION= response — starting 1.5 s stability wait');
        this._atOnboardTimerSource = 'version';
        this._atOnboardTimer = setTimeout(() => {
          this._atOnboardTimer = null;
          this._atOnboardTimerSource = null;
          if (this._onboardBlocked || this._resetLoopDetected) return;
          this._firstEnter = false;
          this._secondEnter = false;
          console.log('AT#ONBOARD sending after version confirmed');
          this.send('AT#ONBOARD')
            .catch(err => console.log('AT#ONBOARD (version-triggered) ERROR:', err.message || err));
        }, 1500);
      }
    }

    // ── AT#ONBOARD stability gate ─────────────────────────────────────────────
    // The device firmware triggers a BLE auto-reset whenever Chrome is the only
    // connection (single handle shared by HID+UART).  On each reset the device
    // reconnects and again says "Waiting for instructions", but immediately
    // follows with "BLE HID is not connected" while the reset cycle is still in
    // progress.  Sending AT#ONBOARD during that window triggers another reset.
    // We break the loop by waiting 1.5s: if "BLE HID is not connected" arrives
    // in that window we cancel; if it does NOT arrive the connection is stable.
    if (String(value)?.includes('Waiting for instructions') && !this._onboardBlocked) {
      // Fallback already cancelled at top of handler; just start the stability timer.
      // Also clear any pending post-reconnect AT#ONBOARD — this message takes priority.
      this._atOnboardPending = false;
      if (this._atOnboardTimer) {
        clearTimeout(this._atOnboardTimer);
        this._atOnboardTimer = null;
      }
      this._atOnboardTimerSource = 'waiting';
      this._atOnboardTimer = setTimeout(() => {
        this._atOnboardTimer = null;
        this._atOnboardTimerSource = null;
        this._firstEnter = false;
        this._secondEnter = false;
        this.send('AT#ONBOARD')
          .catch((err) =>
            console.log('this.send(AT#ONBOARD) ERROR ===', err.message || err)
          );
      }, 1500);
    }

    if (String(value)?.includes('BLE HID is not connected')) {
      // Warn the UI (toast notification)
      if (!this._onboardBlocked && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ble-hid-disconnected'));
      }

      // Cancel any pending AT#ONBOARD — sending now would trigger another reset.
      // Start a recovery timer (6 s) to send it once the HID reset has settled.
      // The GATT connection usually stays alive during the HID reset, so we cannot
      // rely on _handleDisconnection to fire; we need this timer as the primary path.
      // If the GATT does drop before the timer fires, _handleDisconnection will cancel
      // this timer and the reconnect path takes over instead.
      //
      // IMPORTANT: do NOT cancel and restart the timer if it is already running.
      // The device sends "BLE HID is not connected" repeatedly during its reset
      // cycle (every 2-3 s).  Resetting the 6 s countdown on each message means
      // the timer is pushed forward indefinitely and AT#ONBOARD is never sent.
      // Instead, only start the timer on the first occurrence; let it run to
      // completion.  The #VERSION= handler will fire AT#ONBOARD even sooner if
      // the device responds to AT#VERSION? before the 6 s window elapses.
      if (!this._onboardBlocked) {
        this._atOnboardPending = true;
        // If a 'waiting' timer is running (1.5 s stability gate from "Waiting for
        // instructions"), cancel it — we know the connection is NOT yet stable.
        // If a 'ble-hid' timer is already counting down, leave it alone; restarting
        // it would push the 6 s window forward indefinitely and AT#ONBOARD would
        // never fire (this was the original bug). Any other source ('version' or
        // 'reconnect') means we are past the HID-reset phase — leave those alone too.
        if (this._atOnboardTimer && this._atOnboardTimerSource === 'waiting') {
          clearTimeout(this._atOnboardTimer);
          this._atOnboardTimer = null;
          this._atOnboardTimerSource = null;
          console.log('AT#ONBOARD stability timer cancelled — BLE HID reset in progress');
        }
        if (!this._atOnboardTimer) {
          console.log('AT#ONBOARD deferred — BLE HID not stable yet, scheduling retry in 6 s');
          this._atOnboardTimerSource = 'ble-hid';
          this._atOnboardTimer = setTimeout(() => {
            this._atOnboardTimer = null;
            this._atOnboardTimerSource = null;
            this._atOnboardPending = false;
            if (this._onboardBlocked || this._resetLoopDetected) return;
            this._firstEnter = false;
            this._secondEnter = false;
            console.log('AT#ONBOARD retry — BLE HID reset window elapsed');
            this.send('AT#ONBOARD')
              .catch((err) => console.log('AT#ONBOARD retry ERROR:', err.message || err));
          }, 6000);
        }
      }

      // ── Reset loop detection ───────────────────────────────────────────────
      // Count occurrences within a 15-second sliding window. Three hits means
      // the device is stuck in an infinite BLE reset cycle.
      if (!this._resetLoopDetected) {
        const now = Date.now();
        if (!this._bleHidWarningWindowStart || now - this._bleHidWarningWindowStart > 15000) {
          this._bleHidWarningWindowStart = now;
          this._bleHidWarningCount = 1;
        } else {
          this._bleHidWarningCount++;
        }
        if (this._bleHidWarningCount >= 3) {
          this._resetLoopDetected = true;
          this._stopReconnecting = true;
          this._atOnboardPending = false;
          console.log('BLE reset loop detected after', this._bleHidWarningCount, 'cycles — notifying UI');
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('device-reset-loop'));
          }
        }
      }

      // Query firmware version if we don't have it yet
      if (!this._currentVersion) {
        this.send('AT#VERSION?')
          .catch(err => console.log('AT#VERSION? ERROR:', err.message || err));
      }
    }

    if (String(value)?.includes('Enter:') && (!this._firstEnter || !this._secondEnter) && !this._isSendingData) {
      setTimeout(() => {
        // Re-check at send time — setDataToDevice may have started during the 2s delay
        if (this._isSendingData) return;
        const response = !this._firstEnter ? this._onboardEnterResponses[0] : this._onboardEnterResponses[1];
        this.send(response)
          .catch((err) =>
            console.log(`this.send(${response}) ERROR ===`, err.message || err)
          );
        if (!this._firstEnter) {
          this._firstEnter = true;
        } else if (!this._secondEnter) {
          this._secondEnter = true;
        }
      }, 2000);
    }

    for (const c of value) {
      if (c === this._receiveSeparator) {
        const data = this._receiveBuffer.trim();
        this._receiveBuffer = '';
        if (data) {
          this.receive(data);
        }
      } else {
        this._receiveBuffer += c;
      }
    }
  }

  /**
   * Write to characteristic.
   * @param {Object} characteristic
   * @param {string} data
   * @return {Promise}
   * @private
   */
  _writeToCharacteristic(characteristic, data) {
    const encoded = new TextEncoder().encode(data);
    // Retry up to 3 times if a concurrent GATT operation is already in progress.
    // This handles the race where a device notification fires a new send() call
    // before a previous writeValue() has resolved.
    const attempt = (tries) =>
      characteristic.writeValue(encoded).catch(async (err) => {
        if (tries > 0 && err?.message?.includes('GATT operation already in progress')) {
          await new Promise(r => setTimeout(r, 200));
          return attempt(tries - 1);
        }
        throw err;
      });
    return attempt(3);
  }

  /**
   * Log.
   * @param {Array} messages
   * @private
   */
  _log(...messages) {
    console.log(...messages); // eslint-disable-line no-console
  }

  /**
   * Split by length.
   * @param {string} string
   * @param {number} length
   * @return {Array}
   * @private
   */
  static _splitByLength(string, length) {
    return string.match(new RegExp('(.|[\r\n]){1,' + length + '}', 'g'));
  }
}

// Export class as a module to support requiring.
/* istanbul ignore next */
// if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
//   module.exports = BluetoothTerminal;
// }
